import { fromPairs, keyBy, union } from 'lodash';
import { ProjectId } from 'api/models';
import { Project as ApiProject, ProjectPhase } from 'api/project';
import { logger } from 'services';
import {
  Content,
  Empty,
  Loaded,
  Loading,
  RefreshableContent,
  Refreshing,
} from 'stores/core';

export interface Project
  extends Pick<ApiProject, 'projectId' | 'projectName' | 'partners'> {
  customer: number;
  phases: ProjectPhase[];
}

export type ProjectsStore = {
  ids: RefreshableContent<number[]>;
  byId: Record<number, Content<Project>>;
};

const EMPTY: ProjectsStore = {
  byId: {},
  ids: Empty(),
};

const load =
  () =>
  ({ byId, ids }: ProjectsStore): ProjectsStore => ({
    byId,
    ids:
      ids[0] === 'refreshing' || ids[0] === 'loading'
        ? ids
        : ids[0] === 'loaded'
        ? Refreshing(ids)
        : Loading(),
  });

const putAll = (projects: ApiProject[]) => (): ProjectsStore => {
  const ids = projects.map(({ projectId }) => projectId);
  const transformed = projects.map((project) => {
    /* TODO:: Remove this condition once we find the root cause for test case failure */
    if (!project.customer) {
      try {
        // eslint-disable-next-line no-console
        console.error(
          `Customer is missing in the project: ${JSON.stringify(
            project,
          )} ---- projects payload: ${JSON.stringify(projects)}`,
        );
      } catch (error) {
        logger.error(' Error while logging', { error });
      }
    }

    return {
      phases: project.projectPhases,
      projectId: project.projectId,
      projectName: project.projectName,
      customer: project.customer
        ? project.customer.companyId
        : 0 /* TODO:: Remove this condition once we find the root cause for test case failure */,
      partners: project.partners,
    };
  });
  const lookup = keyBy(transformed, 'projectId') as Record<number, Project>;

  return {
    byId: fromPairs(ids.map((id) => [id, Loaded(lookup[id])])),
    ids: Loaded(ids),
  };
};

const updated =
  (project: Project) =>
  ({ ids, byId }: ProjectsStore): ProjectsStore => ({
    ids: Loaded(union(ids[1] ?? [], [project.projectId])),
    byId: {
      ...byId,
      [project.projectId]: Loaded(project),
    },
  });

const loaded = ({ ids: [status] }: ProjectsStore) =>
  status === 'loaded' || status === 'refreshing';
const refreshing = ({ ids: [status] }: ProjectsStore) =>
  status === 'refreshing';

const toList = ({ ids: [, projects = []], byId }: ProjectsStore) =>
  projects
    .map((x) => byId[x])
    .filter(Boolean)
    .map(([, x]) => x)
    .filter(Boolean)
    .map((x) => x!);

const get =
  ([, projectId]: ProjectId) =>
  ({ byId }: ProjectsStore) =>
    byId[projectId] ?? Empty();

export const ProjectsStore = {
  empty: () => EMPTY,
  load,
  putAll,
  loaded,
  toList,
  get,
  refreshing,
  updated,
};
